import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'

import Icon, { type SemanticIconName } from '../Icon'
import Search from '../Search'
import Typography from '../Typography'

import './GroupedNav.scss'

export type GroupedNavItem = {
  key: string
  label: string
  iconName: SemanticIconName
}

export type GroupedNavGroup = {
  heading: string
  items: GroupedNavItem[]
}

export type GroupedNavProps = {
  groups: GroupedNavGroup[]
  value: string | null
  onChange: (key: string) => void
  searchPlaceholder?: string
}

const GroupedNav: React.FC<GroupedNavProps> = ({
  groups,
  value,
  onChange,
  searchPlaceholder = 'Search',
}) => {
  const [query, setQuery] = useState('')

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return groups
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.label.toLowerCase().includes(q)),
      }))
      .filter((group) => group.items.length > 0)
  }, [groups, query])

  return (
    <Box component='nav' className='grouped-nav'>
      <Box className='grouped-nav__search'>
        <Search
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
        />
      </Box>
      <Box className='grouped-nav__groups'>
        {filteredGroups.map((group) => (
          <Box key={group.heading} className='grouped-nav__group'>
            <Typography variant='overline' className='grouped-nav__heading'>
              {group.heading}
            </Typography>
            <Box className='grouped-nav__items'>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type='button'
                  className={`grouped-nav__item${
                    value === item.key ? ' grouped-nav__item--active' : ''
                  }`}
                  onClick={() => onChange(item.key)}
                >
                  <Icon name={item.iconName} size={18} className='grouped-nav__item-icon' />
                  <Typography variant='body2' className='grouped-nav__item-label'>
                    {item.label}
                  </Typography>
                </button>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default GroupedNav
